import * as React from "react";
import { InputText } from "primereact/inputtext";
import { Checkbox } from "primereact/checkbox";

import { useAppSelector } from "../../redux/store.ts";
import { addOptionDialogFactory, isAddNewCompanyTarget } from "./common.tsx";
import {
  api,
  CompanyCreate,
  CreateCompanyApiCompaniesPostApiArg,
  CreateCompanyApiCompaniesPostApiResponse,
  UpdateCompanyApiCompaniesCompanyIdPutApiArg,
} from "../../api";

type CompanyEditFormProps = {
  formContent: CompanyCreate;
  setFormContent: React.Dispatch<React.SetStateAction<CompanyCreate>>;
  lockRoles?: Pick<
    CompanyCreate,
    "is_buyer" | "is_transporter" | "is_supplier"
  >;
};

const CompanyEditForm = ({
  formContent,
  setFormContent,
  lockRoles = { is_buyer: false, is_transporter: false, is_supplier: false },
}: CompanyEditFormProps) => (
  <>
    <div className="field">
      <label>
        Nazwa
        <span style={{ color: "red" }}> *</span>
      </label>
      <InputText
        value={formContent.name}
        onChange={(e) =>
          setFormContent((prev) => ({
            ...prev,
            name: e.target.value,
          }))
        }
        className="w-full"
        placeholder="Wpisz nazwę"
      />
    </div>
    <div className="field">
      <label>
        Kod rejestracyjny <span style={{ color: "red" }}> *</span>
      </label>
      <InputText
        value={formContent.registry_code}
        onChange={(e) =>
          setFormContent((prev) => ({
            ...prev,
            registry_code: e.target.value,
          }))
        }
        className="w-full"
        placeholder="Wpisz kod rejestracyjny"
      />
    </div>
    <div className="field">
      <label>Adres prawny</label>
      <InputText
        value={formContent.legal_address}
        onChange={(e) =>
          setFormContent((prev) => ({
            ...prev,
            legal_address: e.target.value,
          }))
        }
        className="w-full"
        placeholder="Wpisz nazwę"
      />
    </div>
    <div className="field-checkbox flex-container">
      <Checkbox
        inputId="is_supplier"
        checked={formContent.is_supplier}
        disabled={lockRoles.is_supplier}
        onChange={(e) =>
          setFormContent((prev) => ({
            ...prev,
            is_supplier: e.checked ?? false,
          }))
        }
      />
      <label htmlFor="is_supplier">Zakup</label>
    </div>
    <div className="field-checkbox flex-container">
      <Checkbox
        inputId="is_transporter"
        checked={formContent.is_transporter}
        disabled={lockRoles.is_transporter}
        onChange={(e) =>
          setFormContent((prev) => ({
            ...prev,
            is_transporter: e.checked ?? false,
          }))
        }
      />
      <label htmlFor="is_transporter">Przewoznik</label>
    </div>
    <div className="field-checkbox flex-container">
      <Checkbox
        inputId="is_buyer"
        checked={formContent.is_buyer}
        disabled={lockRoles.is_buyer}
        onChange={(e) =>
          setFormContent((prev) => ({
            ...prev,
            is_buyer: e.checked ?? false,
          }))
        }
      />
      <label htmlFor="is_buyer">Sprzedaz</label>
    </div>
  </>
);

const emptyCompany = {
  name: "",
  registry_code: "",
  legal_address: "",
  is_supplier: true,
  is_transporter: true,
  is_buyer: true,
} as const satisfies CompanyCreate;

const CompanyAddOptionDialog = addOptionDialogFactory<
  CompanyCreate,
  | CreateCompanyApiCompaniesPostApiArg
  | UpdateCompanyApiCompaniesCompanyIdPutApiArg,
  CreateCompanyApiCompaniesPostApiResponse,
  CompanyEditFormProps
>({
  defaultValues: emptyCompany,
  FormComponent: CompanyEditForm,
  useMutation: () => {
    const [triggerCreate, createState] =
      api.endpoints.createCompanyApiCompaniesPost.useMutation();
    const [triggerUpdate, updateState] =
      api.endpoints.updateCompanyApiCompaniesCompanyIdPut.useMutation();

    // Choose the correct mutation based on presence of id
    const trigger = (
      args:
        | CreateCompanyApiCompaniesPostApiArg
        | UpdateCompanyApiCompaniesCompanyIdPutApiArg,
    ) => {
      if ("companyId" in args && "companyUpdate" in args) {
        return triggerUpdate(args);
      } else {
        return triggerCreate(args);
      }
    };

    return [
      trigger,
      {
        isLoading: createState.isLoading || updateState.isLoading,
        reset: () => {
          createState.reset?.();
          updateState.reset?.();
        },
        originalArgs: undefined,
      },
    ] as const;
  },
  createMutationArg: (formContent, rowId) => {
    if (rowId) {
      // Update
      return {
        companyId: rowId,
        companyUpdate: formContent,
      };
    } else {
      // Create
      return {
        companyCreate: formContent,
      };
    }
  },
  validate: (formContent) => !!(formContent.name && formContent.registry_code),
  headerText: "Edytuj spółkę",
  makeFormProps: ({ rowId, initialValues }) => ({
    lockRoles:
      initialValues && rowId
        ? { is_buyer: false, is_transporter: false, is_supplier: false }
        : {
            is_buyer: initialValues?.is_buyer ?? false,
            is_transporter: initialValues?.is_transporter ?? false,
            is_supplier: initialValues?.is_supplier ?? false,
          },
  }),
});

export const CompanyAddOption: React.FC = () => {
  const { target } = useAppSelector((state) => state.addNew);
  if (!isAddNewCompanyTarget(target)) {
    return null;
  }
  return (
    <CompanyAddOptionDialog
      rowId={target.id}
      field={target.field}
      initialValues={target.initialValues}
    />
  );
};
